import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { CalendarBlank, CheckCircle, MapPin } from '@phosphor-icons/react';

export default function Rsvp() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', batch_year: '', website: '' });

  useEffect(() => {
    api.get(`/advancement/rsvp/${code}`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e)));
  }, [code]);

  const submit = async (going) => {
    setBusy(true); setError('');
    try {
      await api.post(`/advancement/rsvp/${code}`, { ...form, going });
      setDone(going);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="rsvp-card">
        <div className="flex items-center gap-2 mb-1">
          <CalendarBlank size={22} className="text-orange-600" weight="fill" />
          <h1 className="font-display text-xl font-extrabold tracking-tight">{info ? info.school_name : error ? 'Event not found' : 'Loading…'}</h1>
        </div>
        {info && (
          <>
            <h2 className="font-semibold text-lg mt-2" data-testid="rsvp-event-title">{info.title}</h2>
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
              {info.date}{info.time ? ` · ${info.time}` : ''}
              {info.location && <span className="flex items-center gap-0.5 ms-2"><MapPin size={13} />{info.location}</span>}
            </p>
            {info.description && <p className="text-sm text-slate-600 mt-2">{info.description}</p>}
            <p className="text-xs text-emerald-700 mt-2">{info.going} alumni going so far</p>

            {done !== null ? (
              <div className="text-center py-6" data-testid="rsvp-done">
                <CheckCircle size={44} weight="fill" className={done ? 'text-emerald-500 mx-auto mb-2' : 'text-slate-300 mx-auto mb-2'} />
                <p className="font-semibold">{done ? "You're on the list — see you there!" : 'Response recorded — sorry you can’t make it.'}</p>
              </div>
            ) : (
              <div className="space-y-3 mt-4">
                <input data-testid="rsvp-name-input" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input data-testid="rsvp-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input data-testid="rsvp-batch-input" placeholder="Batch year (optional)" value={form.batch_year} onChange={(e) => setForm({ ...form, batch_year: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />
                {error && <p className="text-xs text-rose-600" data-testid="rsvp-error">{error}</p>}
                <div className="flex gap-2">
                  <button data-testid="rsvp-going-button" disabled={!form.name || !form.email || busy} onClick={() => submit(true)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-40">I'm going</button>
                  <button data-testid="rsvp-decline-button" disabled={!form.name || !form.email || busy} onClick={() => submit(false)}
                    className="flex-1 border border-slate-300 hover:bg-slate-50 rounded-md py-2.5 text-sm disabled:opacity-40">Can't make it</button>
                </div>
              </div>
            )}
          </>
        )}
        {!info && error && <p className="text-sm text-rose-600 mt-3" data-testid="rsvp-notfound">{error}</p>}
      </div>
    </div>
  );
}
